/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import {
  Box,
  VStack,
  Flex,
  Separator,
  Grid,
  GridItem,
  Icon,
  Center,
  Stack,
  Dialog,
  DialogOpenChangeDetails,
  Portal,
  CloseButton,
} from 'chakra-v3';
import { FaFilePdf, FaYoutube } from 'react-icons/fa';
import { Text } from './Text';
import { Stepper } from './Stepper';
import { MdOutlineOpenInNew } from 'react-icons/md';
import CardShell from './CardShell';
import { Button } from './Index';

export interface TutorialStep {
  stepNumber: number;
  title: string;
  description: string;
}

const getVideoId = (url: string) => {
  return new URL(url).searchParams.get('v') ?? '';
};

interface YouTubeDialogProps {
  videoId: string;
  open: boolean;
  onOpenChange: (e: DialogOpenChangeDetails) => void;
}

export function YouTubeDialog({ videoId, open, onOpenChange }: YouTubeDialogProps) {
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;

  return (
    <Portal>
      <Dialog.Root open={open} onOpenChange={onOpenChange} size="xl">
        <Dialog.Backdrop zIndex="overlay" />
        <Dialog.Positioner>
          <Dialog.Content bg="bg.primary">
            <Dialog.Header>
              <Dialog.CloseTrigger asChild>
                <Button variant="outline" asChild>
                  <CloseButton size="sm" />
                </Button>
              </Dialog.CloseTrigger>
              <Dialog.Title>
                <Text variant="projectTitle">Tutorial Video</Text>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <iframe
                src={embedUrl}
                width="100%"
                height="450px"
                allow="autoplay; encrypted-media"
                title="Tutorial Video"
                allowFullScreen
              />
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Portal>
  );
}

const WatchYouTubeTutorialCard: React.FC<{ videoId: string }> = ({ videoId }) => {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <VStack
        onClick={() => setOpen(true)}
        cursor="pointer"
        gap="20px"
        p="24px"
        w="240px"
        alignItems="center"
        border="1px solid"
        borderColor="brand.teal"
        borderRadius="4px"
        bg="radial-gradient(99.74% 65.32% at 48.71% 100%, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)"
      >
        <Flex w="100%" h="124px" bg="brand.tealDark" borderRadius="4px" alignItems="center" justifyContent="center">
          <Icon as={FaYoutube} w="80px" h="56px" />
        </Flex>

        <Text size="md" color="text.primary">
          Watch YouTube Tutorial
        </Text>
      </VStack>

      <YouTubeDialog videoId={videoId} open={open} onOpenChange={(e) => setOpen(e.open)} />
    </>
  );
};

const TutorialCard: React.FC<TutorialStep & { index: number; maxSteps: number }> = ({
  stepNumber,
  title,
  description,
  index,
  maxSteps,
}) => (
  <Flex gap="20px" alignItems="flex-start">
    <Stepper number={stepNumber} showConnector={index < maxSteps - 1} />
    <VStack gap="8px" alignItems="flex-start">
      <Text as="h3" size="md" fontWeight="600" color="brand.secondaryBlue2">
        {title}
      </Text>
      <Text variant="body" size="md" color="text.primary" fontWeight="400">
        {description}
      </Text>
    </VStack>
  </Flex>
);

const ViewPdfCard: React.FC<{ link: string }> = ({ link }) => (
  <a href={link} target="_blank" rel="noopener noreferrer">
    <VStack
      gap="20px"
      p="24px"
      w="240px"
      alignItems="center"
      border="1px solid"
      borderColor="brand.teal"
      borderRadius="4px"
      bg="radial-gradient(99.74% 65.32% at 48.71% 100%, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)"
    >
      <Flex w="100%" h="124px" bg="brand.tealDark" borderRadius="4px" alignItems="center" justifyContent="center">
        <Icon as={FaFilePdf} w="60px" h="60px" color="text.primary" />
      </Flex>
      <Flex gap="12px" alignItems="center">
        <Text size="md" color="text.primary">
          View Detailed Guidelines
        </Text>
        <MdOutlineOpenInNew />
      </Flex>
    </VStack>
  </a>
);

export interface TutorialProps {
  steps: TutorialStep[];
  youtubeLink: string;
  pdfLink: string;
  onStart?: () => void;
  showStartButton?: boolean;
}

export const Tutorial: React.FC<TutorialProps> = ({ steps, youtubeLink, pdfLink, onStart, showStartButton }) => {
  return (
    <Center width="100%" direction="column" gap="40px">
      <CardShell>
        <Stack padding="20px">
          <Grid templateColumns="1fr auto 1fr" gap="60px" alignItems="stretch" margin="50px">
            <GridItem>
              <VStack flex="1" gap="0" maxW="564px" alignItems="flex-start">
                {steps.map((step, index) => (
                  <Box key={step.stepNumber} h="200px" data-section-container>
                    <TutorialCard {...step} maxSteps={steps.length} index={index} />
                  </Box>
                ))}
              </VStack>
            </GridItem>

            <GridItem>
              <Separator orientation="vertical" h="100%" borderColor="connector.default" />
            </GridItem>

            <GridItem>
              <VStack flex="1" gap="20px" maxW="240px" justifyContent="center" margin="auto">
                <WatchYouTubeTutorialCard videoId={getVideoId(youtubeLink)} />
                <ViewPdfCard link={pdfLink} />
              </VStack>
            </GridItem>
          </Grid>
          <Flex flexDirection="row-reverse">
            <Button onClick={onStart} visibility={showStartButton ? 'visible' : 'hidden'}>
              Start Task
            </Button>
          </Flex>
        </Stack>
      </CardShell>
    </Center>
  );
};

export default Tutorial;
